/**
 * CashflowCalculator.calculate: payments by category over the calculation window's buckets,
 * widened back to the month of the earliest active start, plus the view-window slice.
 */
import { addDays, addMonths, monthsBetween, startOfMonth, type Day } from '../core/calendar.ts';
import { add, dec, lt, round2, sub, ZERO, type Decimal } from '../core/decimal.ts';
import { bucketIndexContaining, timeBuckets } from '../forecast/window.ts';
import { depreciatingAssetMonthlyCosts } from '../models/depreciating-asset.ts';
import { propertyMonthlyCosts } from '../models/property.ts';
import type { Asset, CalcContext, DepreciatingAsset, Property, Series, Transaction, TransactionType, Window } from '../types/domain.ts';
import { debtPayment, debtPaymentDates } from './debt-projector.ts';
import { paidOn } from '../models/transaction.ts';
import { calculatePayments, type OccurrenceAmount } from './payment-calculator.ts';
import { runPot } from './pot.ts';
import { assetSale, propertySale, saleDates, settleSoldAssetDebts, unifiedAssetSale } from './sales.ts';

const DEFAULT_RETURN_PERCENT = dec(6);

export type CashflowType = TransactionType | 'disposable';

/** The order the contract lists the series in. */
export const CASHFLOW_ORDER: readonly CashflowType[] = [
  'income', 'expense', 'investment', 'debt', 'property', 'depreciatingAsset', 'disposable',
];

export type CashflowSeries = Readonly<Record<CashflowType, Series>>;

export interface CashflowForecast {
  readonly payments: CashflowSeries;
  readonly windowed: CashflowSeries;
  readonly calculationWindow: Window;
}

export interface CashflowInputs {
  readonly transactions: readonly Transaction[];
  readonly properties: readonly Property[];
  readonly depreciatingAssets: readonly DepreciatingAsset[];
  /** Unified assets; one that is also a legacy record is sold through the legacy record. */
  readonly assets: readonly Asset[];
  readonly calculationWindow: Window;
  readonly viewWindow: Window | null;
  /** The plan's return, for pots without their own rate: what a withdrawal can take depends on it (R23). Default 6. */
  readonly expectedReturnPercent?: Decimal;
}

const minDay = (days: readonly Day[]): Day | null => (days.length === 0 ? null : days.reduce((a, b) => (b < a ? b : a)));

function earliestStart(inputs: CashflowInputs): Day | null {
  return minDay([
    ...inputs.transactions.filter((t) => t.isActive).map((t) => t.startDate),
    ...inputs.properties.filter((p) => p.isActive).map((p) => p.startDate),
    ...inputs.depreciatingAssets.filter((a) => a.isActive).map((a) => a.purchaseDate),
  ]);
}

/** A unified asset sale's net proceeds, booked as a negative payment in the sale month. */
interface UnifiedSale {
  readonly month: Day;
  readonly isProperty: boolean;
  readonly netProceeds: Decimal;
}

/**
 * One amount per month, on the 1st, from the start of the window's first month while before
 * its end (whatever its interval): the legacy items, less any unified sale proceeds booked that
 * month, round2ed.
 */
function monthlyAmounts(window: Window, sales: readonly UnifiedSale[], amountFor: (month: Day) => Decimal): Series {
  const buckets = [];
  for (let month = startOfMonth(window.start); month < window.end; month = addMonths(month, 1)) {
    const total = sales.filter((sale) => sale.month === month).reduce((t, sale) => sub(t, sale.netProceeds), amountFor(month));
    buckets.push({ start: month, end: addMonths(month, 1), amount: round2(total) });
  }
  return buckets;
}

/** Monthly amounts summed into the window's buckets: each month goes to the bucket containing its 1st (R7). */
function rebucket(monthly: Series, window: Window): Series {
  const buckets = timeBuckets(window);
  const totals: Decimal[] = buckets.map(() => ZERO);
  for (const month of monthly) {
    const index = bucketIndexContaining(buckets, month.start);
    if (index !== null) totals[index] = add(totals[index]!, month.amount);
  }
  return buckets.map((bucket, i) => ({ ...bucket, amount: totals[i]! }));
}

/** One owned item's contribution to a month: −proceeds in its sale month, nothing after, otherwise its costs. */
function ownedItemAmount(
  month: Day,
  owned: Day,
  sale: { readonly date: Day } | null,
  proceeds: () => Decimal,
  costs: () => Decimal,
): Decimal {
  if (month < owned) return ZERO;
  if (sale !== null && month >= startOfMonth(sale.date)) {
    return month === startOfMonth(sale.date) ? sub(ZERO, proceeds()) : ZERO;
  }
  return costs();
}

/** The active debts linked to one asset, and the clock for what they owe. */
interface Linked {
  readonly debts: readonly Transaction[];
  readonly now: Day;
}

const linkedTo = (linked: Linked, id: string): readonly Transaction[] => linked.debts.filter((d) => d.linkedAssetId === id);

const propertyPayments = (properties: readonly Property[], linked: Linked, sales: readonly UnifiedSale[], window: Window): Series =>
  monthlyAmounts(window, sales, (month) =>
    properties.filter((p) => p.isActive).reduce<Decimal>((total, p) => add(total, ownedItemAmount(
      month,
      p.startDate,
      p.saleEvent,
      () => (p.saleEvent === null ? ZERO : propertySale(p, p.saleEvent.date, linkedTo(linked, p.id), linked.now)?.netProceeds ?? ZERO),
      () => propertyMonthlyCosts(p),
    )), ZERO),
  );

const leaseReturned = (asset: DepreciatingAsset, month: Day): boolean => {
  const f = asset.finance;
  return f !== null && f.type === 'lease' && f.termMonths !== null && f.endAction === 'return'
    && monthsBetween(asset.purchaseDate, month) >= f.termMonths;
};

const assetPayments = (assets: readonly DepreciatingAsset[], linked: Linked, sales: readonly UnifiedSale[], window: Window): Series =>
  monthlyAmounts(window, sales, (month) =>
    assets.filter((a) => a.isActive).reduce<Decimal>((total, a) => add(total, ownedItemAmount(
      month,
      a.purchaseDate,
      a.saleEvent,
      () => (a.saleEvent === null ? ZERO : assetSale(a, a.saleEvent.date, linkedTo(linked, a.id), linked.now)?.netProceeds ?? ZERO),
      () => (leaseReturned(a, month) ? ZERO : depreciatingAssetMonthlyCosts(a)),
    )), ZERO),
  );

/**
 * Each active debt's payments on its own payment dates (R14, R15), at what the balance takes
 * (DebtProjector.payment); each debt's buckets rounded 2dp, then summed in debt order.
 */
function balanceAwareDebtPayments(debts: readonly Transaction[], window: Window, ctx: CalcContext): Series {
  const buckets = timeBuckets(window);
  const totals = debts.filter((d) => d.isActive).reduce<readonly Decimal[]>((acc, debt) => {
    const paid: Decimal[] = buckets.map(() => ZERO);
    for (const date of debtPaymentDates(debt, window.end)) {
      const index = bucketIndexContaining(buckets, date);
      if (index !== null) paid[index] = add(paid[index]!, debtPayment(debt, date, ctx.now));
    }
    return acc.map((total, i) => add(total, round2(paid[i]!)));
  }, buckets.map(() => ZERO));
  return buckets.map((bucket, i) => ({ ...bucket, amount: totals[i]! }));
}

/**
 * What each investment payment takes from cash (R13, R23): the saver's own R11 amount, or for a
 * withdrawal what its pot still holds, so cash stops receiving once the pot is empty.
 */
function investmentCash(investments: readonly Transaction[], incomes: readonly Transaction[], returnPercent: Decimal, end: Day): OccurrenceAmount {
  const withdrawn = new Map(
    investments
      .filter((t) => t.isActive && lt(t.amount, ZERO))
      .map((t) => [t.id, new Map(runPot(t, incomes, returnPercent, [], addDays(end, -1)).payments.map((p) => [p.date, p.cash]))] as const),
  );
  return (t, date) => withdrawn.get(t.id)?.get(date) ?? paidOn(t, date);
}

/** income − expense − investment − debt − property − asset per bucket, round2ed (all share the window's buckets). */
function disposablePayments(income: Series, outgoings: readonly Series[]): Series {
  return income.map((bucket, i) => ({
    ...bucket,
    amount: round2(outgoings.reduce((total, s) => sub(total, s[i]!.amount), bucket.amount)),
  }));
}

/** From the bucket containing the view start (R7); empty when every bucket ends by then. */
const fromViewStart = (series: Series, viewStart: Day): Series => {
  const index = series.findIndex((b) => b.end > viewStart);
  return index < 0 ? [] : series.slice(index);
};

const ofType = (transactions: readonly Transaction[], type: TransactionType) => transactions.filter((t) => t.type === type);

/**
 * The sales of `assets` that are active and not also a legacy record, each with the net
 * proceeds of CashflowCalculator.unifiedAssetSale against its active linked debts.
 */
function unifiedSales(assets: readonly Asset[], linked: Linked): readonly UnifiedSale[] {
  return assets.flatMap((asset) => {
    const sale = unifiedAssetSale(asset, linkedTo(linked, asset.id), linked.now);
    if (asset.saleEvent === null || sale === null) return [];
    return [{ month: startOfMonth(asset.saleEvent.date), isProperty: asset.assetClass === 'property', netProceeds: sale.netProceeds }];
  });
}

/**
 * CashflowCalculator.calculate. Debts linked to any sold asset stop at the sale, whose net
 * proceeds (less what they owe) are booked in the property (property class) or
 * depreciatingAsset series (R2, R9).
 */
export function calculateCashflow(inputs: CashflowInputs, ctx: CalcContext): CashflowForecast {
  const legacyIds = new Set([...inputs.properties.map((p) => p.id), ...inputs.depreciatingAssets.map((a) => a.id)]);
  const assets = inputs.assets.filter((a) => a.isActive && !legacyIds.has(a.id));
  const t = settleSoldAssetDebts(
    inputs.transactions,
    saleDates(inputs.assets, inputs.properties, inputs.depreciatingAssets),
    { onlyDebts: true },
  );
  const linked: Linked = { debts: ofType(t, 'debt').filter((d) => d.isActive), now: ctx.now };

  const requested = inputs.calculationWindow;
  const earliest = earliestStart({ ...inputs, transactions: t });
  const window: Window = {
    ...requested,
    start: startOfMonth(earliest !== null && earliest < requested.start ? earliest : requested.start),
  };
  const sales = unifiedSales(assets, linked);
  const income = calculatePayments(ofType(t, 'income'), window);
  const expense = calculatePayments(ofType(t, 'expense'), window);
  const investment = calculatePayments(
    ofType(t, 'investment'),
    window,
    () => true,
    investmentCash(ofType(t, 'investment'), ofType(t, 'income'), inputs.expectedReturnPercent ?? DEFAULT_RETURN_PERCENT, window.end),
  );
  const debt = balanceAwareDebtPayments(ofType(t, 'debt'), window, ctx);
  const property = rebucket(propertyPayments(inputs.properties, linked, sales.filter((s) => s.isProperty), window), window);
  const depreciatingAsset = rebucket(
    assetPayments(inputs.depreciatingAssets, linked, sales.filter((s) => !s.isProperty), window),
    window,
  );
  const payments: CashflowSeries = {
    income, expense, investment, debt, property, depreciatingAsset,
    disposable: disposablePayments(income, [expense, investment, debt, property, depreciatingAsset]),
  };

  const viewStart = (inputs.viewWindow ?? requested).start;
  const windowed = Object.fromEntries(
    CASHFLOW_ORDER.map((type) => [type, fromViewStart(payments[type], viewStart)]),
  ) as CashflowSeries;
  return { payments, windowed, calculationWindow: window };
}
